import React from 'react';
import { Link } from 'react-router-dom';
import useSeo from './useSeo';
import { GROUPS, SQUADRONS } from '../data/squadrons';
import { KNOWLEDGE, activeMissionTags } from '../data/squadron-knowledge';
import { trackJoinDiscord } from '../lib/analytics';

const SITE = 'https://jointtaskforceheavy.com';
const DISCORD = 'https://discord.gg/sPavY3ZZQH';

// Hub page: unique per-squadron blurbs, each card linking to its own airframe
// page (/squadrons/<slug>). Data lives in src/data/squadrons.js so routes,
// pre-render, and the sitemap all stay in sync from one source; the survey-backed
// operational detail (role, missions, schedule) comes from squadron-knowledge.js.
//
// This page carries the most search impressions on the site and historically the
// worst click-through of any page, so it leads with the concrete comparison a
// pilot is actually searching for rather than with prose.

// First recurring event for a squadron, rendered as a short schedule line.
const scheduleLine = (k) => {
  if (!k || !k.recurring || k.recurring.length === 0) return null;
  const first = k.recurring[0];
  return first.cadence || null;
};

const SquadronsPage = () => {
  const missionTags = activeMissionTags();

  useSeo({
    title: 'DCS Squadrons Recruiting: F-16, Hornet, A-10, F-14 | JTF Heavy',
    description:
      'Eleven DCS World squadrons recruiting now — F-16C, F/A-18C, A-10C II, F-14B, F-4E, C-130J and rotary wing. Compare roles, training nights and qualifications. New pilots welcome regardless of experience.',
    canonical: `${SITE}/squadrons`,
  });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'Squadrons', item: `${SITE}/squadrons` },
        ],
      },
      {
        '@type': 'ItemList',
        name: 'DCS squadrons of Joint Task Force Heavy',
        numberOfItems: SQUADRONS.length,
        itemListElement: SQUADRONS.map((sq, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: `${sq.name} — ${sq.airframe}`,
          url: `${SITE}/squadrons/${sq.slug}`,
        })),
      },
    ],
  };

  return (
    <div>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Hero band */}
      <section className="relative bg-hvy-900 pt-28 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <img src="/logo.webp" alt="JTF Heavy" className="mx-auto h-20 mb-6" />
          <h1 className="font-heading text-4xl md:text-5xl font-bold uppercase tracking-wider text-white">
            Our DCS Squadrons
          </h1>
          <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">
            {SQUADRONS.length} squadrons across four battle groups, flying the full range of
            NATO aircraft. Every one of them is recruiting, and none of them require
            prior jet experience.
          </p>
        </div>
      </section>

      {/* Content */}
      <main className="w-full px-6 py-12 bg-gray-50 bg-[url('bg.webp')] bg-repeat bg-fixed bg-top">
        <div className="max-w-5xl mx-auto">
          {/* Comparison table — the fastest way to answer "which one do I pick?" */}
          <section>
            <h2 className="font-heading text-3xl font-bold uppercase text-hvy-900">
              Compare every squadron
              <span className="block mt-3 h-1 w-16 bg-hvy-accent rounded-full" />
            </h2>
            <p className="mt-3 text-gray-600 max-w-3xl">
              Pick by the aircraft you want to master, the job you want to do, or the night
              you are free to fly. Schedules and qualifications below come from the squadron
              leads themselves.
            </p>

            <div className="mt-8 overflow-x-auto rounded-xl border border-gray-200/70 shadow-sm bg-white/70 backdrop-blur-sm">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-white/60">
                    <th scope="col" className="px-4 py-3 font-semibold text-hvy-900 whitespace-nowrap">Squadron</th>
                    <th scope="col" className="px-4 py-3 font-semibold text-hvy-900 whitespace-nowrap">Aircraft</th>
                    <th scope="col" className="px-4 py-3 font-semibold text-hvy-900">Primary role</th>
                    <th scope="col" className="px-4 py-3 font-semibold text-hvy-900 whitespace-nowrap">Flies</th>
                  </tr>
                </thead>
                <tbody>
                  {SQUADRONS.map((sq) => {
                    const k = KNOWLEDGE[sq.slug];
                    const when = scheduleLine(k);
                    return (
                      <tr key={sq.slug} className="border-b border-gray-100 last:border-0 hover:bg-white/70">
                        <td className="px-4 py-3">
                          <Link
                            to={`/squadrons/${sq.slug}`}
                            className="font-semibold text-hvy-500 hover:text-hvy-700 underline"
                          >
                            {sq.name}
                          </Link>
                        </td>
                        {/* Full airframe name, not airframeShort — the short form
                            renders as "Civ" / "Rotary", which reads as an abbreviation
                            a prospective recruit has to decode. */}
                        <td className="px-4 py-3 whitespace-nowrap text-gray-700">{sq.airframe}</td>
                        <td className="px-4 py-3 text-gray-700">{k && k.role ? k.role : '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{when || 'Ask on Discord'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-sm text-gray-500">
              &ldquo;Ask on Discord&rdquo; means that squadron sets its flying schedule with its
              members rather than on a fixed night.
            </p>
          </section>

          {/* What the force flies — captures mission-specific searches */}
          <section className="mt-16">
            <h2 className="font-heading text-3xl font-bold uppercase text-hvy-900">
              Missions we fly
              <span className="block mt-3 h-1 w-16 bg-hvy-accent rounded-full" />
            </h2>
            <p className="mt-3 text-gray-600 max-w-3xl">
              Between them, our squadrons cover the full mission set — from suppressing air
              defences to inserting troops by helicopter to keeping the force fuelled.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {missionTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-block rounded-full border border-hvy-500/40 bg-white/70 px-3 py-1 text-sm font-medium text-hvy-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          </section>

          {/* Group-by-group cards */}
          {GROUPS.map((group) => (
            <section key={group.name} className="mt-16">
              <h2 className="font-heading text-3xl font-bold uppercase text-hvy-900">
                {group.name}
                <span className="block mt-3 h-1 w-16 bg-hvy-accent rounded-full" />
              </h2>
              <p className="mt-3 text-gray-600 max-w-3xl">{group.blurb}</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                {group.squadrons.map((sq) => {
                  const k = KNOWLEDGE[sq.slug];
                  const when = scheduleLine(k);
                  return (
                    <Link
                      key={sq.slug}
                      to={`/squadrons/${sq.slug}`}
                      className="flex gap-5 bg-white/60 backdrop-blur-sm rounded-xl p-6 shadow-sm border border-gray-200/50 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                    >
                      <img src={sq.logo} alt={`${sq.name} insignia`} className="h-20 w-20 object-contain flex-shrink-0" loading="lazy" />
                      <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900">{sq.name}</h3>
                        <p className="text-sm text-hvy-500 font-medium mb-2">{sq.airframe}</p>
                        <p className="text-gray-600 leading-relaxed">{sq.blurb}</p>

                        {k && k.missions && k.missions.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {k.missions.slice(0, 4).map((m) => (
                              <span key={m} className="inline-block rounded-full bg-hvy-900/5 px-2.5 py-0.5 text-xs font-medium text-hvy-700">
                                {m}
                              </span>
                            ))}
                            {k.missions.length > 4 && (
                              <span className="inline-block px-1 py-0.5 text-xs text-gray-500">
                                +{k.missions.length - 4} more
                              </span>
                            )}
                          </div>
                        )}

                        {when && (
                          <p className="mt-3 text-sm text-gray-600">
                            <span className="font-semibold text-gray-700">Flies:</span> {when}
                          </p>
                        )}

                        <span className="inline-block mt-3 text-sm font-semibold text-hvy-500">
                          View squadron &rarr;
                        </span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          ))}

          <div className="mt-16 pt-8 border-t border-gray-200 text-center text-lg text-gray-700">
            <p className="mb-6">
              Not sure which one fits? Join the Discord and say so — we will point you at the
              squadrons flying what you want, when you are online.
            </p>
            <a
              href={DISCORD}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackJoinDiscord('squadrons-hub')}
              className="inline-block bg-discord hover:bg-discord/90 text-white font-bold py-3 px-8 rounded-lg transition-all duration-200 shadow-lg uppercase tracking-wider"
            >
              Join Our Discord
            </a>
            <p className="mt-8 text-base text-gray-600">
              New to DCS? Read our{' '}
              <Link to="/join-dcs-squadron" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">guide to joining a DCS squadron</Link>{' '}
              or return to the <Link to="/" className="text-hvy-500 font-semibold hover:text-hvy-700 underline">home page</Link>.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SquadronsPage;
